<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader"
    :showLoader="showLoader"
    :showTopRightAreaSlot="renderAccountSelector"
    :applyErrorState="applyErrorState"
	:errorStateMessage="t('accounts.details.detailError')"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *                 Account Selector               *
        ********************************************** -->
        <template #accounts-card-slot-area>
            <account-selector
            v-if="renderAccountSelector"
            :class="{ 'mobile-margin': getMobileAppState }"
            :selectedOption="selectedAccount.id"
            :options="getAccountsBalances"
            @clicked="[selectedAccountId = $event, requestDetails()]"/>
        </template>
       <!-- *********************************************
		*												 *
		*                      Content                   *
		*												 *
		********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
			<div v-if="!showLoader && !applyErrorState" class="details-wrapper">
				<div v-for="(lineGroup, index) in parsedAccountDetails" :key="`lineGroup${lineGroup.id}`" class="group-wrapper">
					<div class="divider-wrapper">
						<divider-generic :icon="lineGroup.icon" :title="lineGroup.label" :applyBorder="index !== 0"/>
						<dropdown-more-options
						v-if="lineGroup.sendButtons && getMobileAppState"
						:options="accountMoreOptions"
						@clicked="accountMoreOptions.find(obj => obj.id === $event).callbackFunction()"/>
					</div>
					<!-- Text Block for each line -->
					<div v-for="line in lineGroup.lines" :key="`lineGroupLine${line,id}`" class="blocks-container">
						<!-- Generic Block -->
						<text-block-generic
						v-if="line.fieldName !== 'account_description' && line.fieldName !== 'account_nib' && line.fieldName !== 'account_iban'"
						:params="line"
						:mask="line.mask"
						:icon="line.icon"
						:colorIconPreset="line.colorIconPreset"
						applyEllipsis/>
						<!-- Copy Block -->
						<text-block-copy
						v-else-if="line.fieldName === 'account_nib' || line.fieldName === 'account_iban'"
						:params="line"/>
						<!-- Editable Block -->
						<text-block-editable
						v-else
                        focusInput
						:params="accountDescriptionParams(line)"
						:requestPayload="accountDescriptionPayload"
						@updateContent="tempDescription = $event"
						@requestSuccessful="accountDescription = $event.description"/>
					</div>
					<!-- Send NBA/IBAN Buttons -->
					<div class="buttons-wrapper" v-if="lineGroup.sendButtons && !getMobileAppState">
						<button-tertiary
						icon="letter"
						:label="t('accounts.details.moreOptions.sendEmail')"
						@clicked="[modalSendType = 'email', showSendInfoModal = true]"/>
						<button-tertiary
						icon="chat"
						:label="t('accounts.details.moreOptions.sendSms')"
						@clicked="[modalSendType = 'sms', showSendInfoModal = true]"/>
					</div>
				</div>
			</div>
        </transition>
		<!-- Error Feedback -->
		<toast-generic
		v-if="showFeedbackToast"
		:toastType="feedbackToastData.toastType"
		:feedbackText="feedbackToastData.text"
		:buttonsParams="feedbackToastData.buttons"
		@hideMe="showFeedbackToast = false"/>
		<!-- Send to email/sms NBA/IBAN Details -->
		<modal-centered-send-info
		v-if="showSendInfoModal"
		onlySendIsAvailableForThisOperation
		emailAction="sendNbaIbanEmailNotification"
		smsAction="sendNbaIbanSMSNotification"
		:sendType="modalSendType"
		:payload="showSendInfoModalData"
		@hideMe="showSendInfoModal = false"/>
	</wrapper-features-templates-one-column>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import AccountSelector from '@/components/generic/selectorsWithDetails/Accounts'
import ToastGeneric from '@/components/generic/toasts/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TextBlockCopy from '@/components/generic/textBlocks/Copy'
import TextBlockEditable from '@/components/generic/textBlocks/Editable'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'

/* VUE & Assets & Helpers */
import { ref, computed } from 'vue'
import { parseLineGroups } from '@/assets/scripts/parsers/genericDetail'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])
/* mapActions & mapGetters */
const { requestAccountsBalances, requestAccountDetails } = mapActions(moduleMapper.ACCOUNTS_DETAILS)
const { getAccountsBalances } = mapGetters(moduleMapper.ACCOUNTS_DETAILS)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const renderAccountSelector = ref(false)
const selectedAccountId = ref(null)
const showSendInfoModal = ref(false)
const modalSendType = ref('')

const selectedAccount = computed(() => {
	return getAccountsBalances.value.find(account => account.id === selectedAccountId.value)
})

const showSendInfoModalData = computed(() => {
	return { accountId: selectedAccountId.value, operationId: '', movementId: '', params: {} }
})

const accountMoreOptions = [
	{
		id: 1,
		text: t('accounts.details.moreOptions.sendEmail'),
		icon: 'letter',
		callbackFunction: () => {
			modalSendType.value = 'email'
			showSendInfoModal.value = true
		}
	}, {
		id: 2,
		text: t('accounts.details.moreOptions.sendSms'),
		icon: 'chat',
		callbackFunction: () => {
			modalSendType.value = 'sms'
			showSendInfoModal.value = true
		}
	}
]
/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/

const requestAccountsBalance = async () => {
	renderAccountSelector.value = false
	showLoader.value = true
	applyErrorState.value = false
	showFeedbackToast.value = false
	cancelAllPendingRequests()

	try {
        await requestAccountsBalances()
        /* Setting Initial Selected Account */
		selectedAccountId.value = getAccountsBalances.value.find(account => { return account.default }).id
		renderAccountSelector.value = true
        requestDetails()
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestAccountsBalance)
	}
}

const requestDetails = async () => {
	showLoader.value = true
	applyErrorState.value = false
	showFeedbackToast.value = false
	accountDescription.value = null
	cancelAllPendingRequests()

	const payload = selectedAccountId.value

	try {
		const response = await requestAccountDetails(payload)
		accountDetails.value = response.data.data.lineGroups
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestDetails)
	}
}

/*************************************************
*                                                *
*                   Details                      *
*                                                *
*************************************************/
const accountDetails = ref(null)
const accountDescription = ref(null)

const parsedAccountDetails = computed(() => {
	const parsedGroups = parseLineGroups(accountDetails.value)

	if (parsedGroups.length) {
		parsedGroups.forEach(group => {
			if (group.id === 4) group.sendButtons = true
		})
	}

	return parsedGroups
})

const accountDescriptionPayload = computed(() => {
	return {
		accountId: selectedAccountId.value,
		data: {
			description: tempDescription.value
		}
	}
})

const tempDescription = ref('')
const accountDescriptionParams = lineInfo => {
	if (!accountDescription.value) accountDescription.value = lineInfo.lineValue

	return {
		label: lineInfo.label,
		title: t('accounts.details.editDescription'),
        placeholder: t('accounts.details.placeholderDescription'),
		content: accountDescription.value,
		maxLength: 25,
		showCounter: true,
		editAction: 'accountsDetails/updateAccountDescription'
	}
}

/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestAccountsBalance()
</script>

<style lang="scss" scoped>
.selector-wrapper {
	margin-bottom: 20px;

	&.mobile-margin {
		margin-bottom: 16px;
	}
}

.details-wrapper {
    @include commonEnteringAnimation;
    width: 100%;

	display: flex;
	flex-direction: column;
	align-items: center;

    .group-wrapper {
        width: 100%;
		max-width: 508px;
        margin-bottom: 24px;

        .blocks-container:not(:last-of-type) {
            margin-bottom: 24px;
        }

        .divider-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;

            position: relative;

            :deep(.dropdown-wrapper) {
                position: absolute;
                top: 13px;
                right: 16px;

                .change-color-fill {
                    fill: #616161; // TODO: Add color on theme variables
                }
            }
        }
    }

    :deep(.divider-generic-wrapper) {
        min-height: 22px;
        margin-bottom: 24px;
        .left-divider-title {
            @include H4-Medium;
            color: #616161; // TODO: Add color on theme variables
        }
    }
}

.buttons-wrapper {
	width: 100%;
	margin-top: 16px;

	display: flex;
	justify-content: flex-start;
    gap: 16px;

	:deep(.button-raised) {
		min-width: 134px;

		&:last-of-type {
			margin-left: 16px;
		}
	}
}
</style>
